import React, { useCallback, useEffect, useState } from 'react';
import { Search, Wrench } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { usersApi } from '../../api/users';
import {
  consumerReconciliationApi,
  PeopleRepairReport,
  PersonPurchase,
} from '../../api/consumer-reconciliation';
import { PayIQUser } from '../../types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';

/**
 * A person's history against what Safe Haven actually took.
 *
 * The ledger repairs lived only in a business account's drawer, so a
 * consumer whose ₦100 of airtime cost ₦97 but showed ₦100 had nowhere to be
 * looked up. Find the person; their airtime and bills recorded at face value
 * are marked, and "Fix" corrects just theirs — from their Safe Haven
 * statement where a line matches, at the commission rate where none does.
 *
 * Below that, the estate-wide repairs that put people's history right, with
 * what each still has to do.
 *
 * Nothing on this page moves money. It corrects what the history says.
 */

const naira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };
const sectionTitle: React.CSSProperties = { margin: '0 0 6px', fontSize: 15, color: 'var(--neutral-900)' };
const sectionNote: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 13,
  color: 'var(--neutral-600)',
  maxWidth: 680,
};

const nameOf = (u: PayIQUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.phoneNumber;

export const ConsumerReconciliation: React.FC = () => {
  usePage('Consumer Reconciliation');
  const toast = useToast();
  const { can } = useAuth();
  const canRepair = can('consumer_reconciliation.repair');

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState<PayIQUser[] | null>(null);

  const [person, setPerson] = useState<PayIQUser | null>(null);
  const [purchases, setPurchases] = useState<PersonPurchase[] | null>(null);
  const [fixing, setFixing] = useState(false);
  const [confirmFix, setConfirmFix] = useState(false);

  const [report, setReport] = useState<PeopleRepairReport | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  const loadRepairs = useCallback(async () => {
    try {
      setReport(await consumerReconciliationApi.repairs());
    } catch (err) {
      toast.error(err, 'Could not read the repairs.');
    }
  }, [toast]);

  useEffect(() => {
    void loadRepairs();
  }, [loadRepairs]);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const result = await usersApi.getUsers({ search: q, limit: 10 });
      setMatches(result.items);
    } catch (err) {
      toast.error(err, 'Could not search people.');
    } finally {
      setSearching(false);
    }
  }

  async function open(user: PayIQUser) {
    setPerson(user);
    setPurchases(null);
    setMatches(null);
    try {
      setPurchases(await consumerReconciliationApi.purchases(user.id));
    } catch (err) {
      toast.error(err, 'Could not read their purchases.');
    }
  }

  async function fix() {
    if (!person) return;
    setFixing(true);
    try {
      const result = await consumerReconciliationApi.repairPurchases(person.id);
      setPurchases(result.rows);
      const fixed = result.fromStatement + result.atRate;
      toast.success(
        fixed === 0
          ? 'Nothing to correct.'
          : `Corrected ${fixed} purchase${fixed === 1 ? '' : 's'}: ` +
              `${result.fromStatement} from their Safe Haven statement, ${result.atRate} at the commission rate.`,
      );
      if (result.statementLines === null) {
        toast.error('Their statement could not be read, so the rate was used.');
      }
      setConfirmFix(false);
      void loadRepairs();
    } catch (err) {
      toast.error(err, 'Could not correct their purchases.');
    } finally {
      setFixing(false);
    }
  }

  async function runRepair(key: string) {
    setRunning(key);
    try {
      const result = await consumerReconciliationApi.runRepair(key);
      const r = result.repairs[0];
      toast.success(
        r ? `${r.label}: ${r.repaired ?? 0} corrected, ${r.outstanding} left.` : 'Done.',
      );
      await loadRepairs();
      if (person) setPurchases(await consumerReconciliationApi.purchases(person.id));
    } catch (err) {
      toast.error(err, 'Could not run that repair.');
    } finally {
      setRunning(null);
    }
  }

  const atFace = purchases?.filter((p) => p.atFaceValue) ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <h3 style={sectionTitle}>Find a person</h3>
        <p style={sectionNote}>
          Their airtime and bills, against what Safe Haven actually took. Safe
          Haven debits face value less its commission (₦97 for ₦100 of
          airtime), so a purchase still showing face value is showing more than
          left their account.
        </p>
        <form onSubmit={search} style={{ display: 'flex', gap: 8, maxWidth: 480 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--neutral-500)',
              }}
            />
            <input
              aria-label="Search people"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email or phone"
              style={{
                width: '100%',
                height: 38,
                padding: '0 12px 0 34px',
                fontSize: 13,
                fontFamily: 'var(--font-family)',
                color: 'var(--neutral-800)',
                background: 'var(--neutral-100)',
                border: '1px solid var(--neutral-300)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </form>

        {matches && (
          <div style={{ marginTop: 16 }}>
            {matches.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>Nobody matches that.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {matches.map((u) => (
                    <tr key={u.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{nameOf(u)}</div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          {u.email ?? u.phoneNumber}
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <Button variant="ghost" onClick={() => void open(u)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {person && (
        <Card padding={20}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 12,
            }}
          >
            <div>
              <h3 style={sectionTitle}>{nameOf(person)} — airtime and bills</h3>
              <p style={{ ...sectionNote, margin: 0 }}>
                The last 50.{' '}
                {purchases &&
                  (atFace.length
                    ? `${atFace.length} still show${atFace.length === 1 ? 's' : ''} face value.`
                    : 'Every one shows what was taken.')}
              </p>
            </div>
            {canRepair && atFace.length > 0 && (
              <Button onClick={() => setConfirmFix(true)} disabled={fixing}>
                <Wrench size={15} style={{ marginRight: 6 }} />
                Fix {atFace.length} purchase{atFace.length === 1 ? '' : 's'}
              </Button>
            )}
          </div>

          {!purchases ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <Spinner />
            </div>
          ) : purchases.length === 0 ? (
            <EmptyState title="No airtime or bills yet." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Purchase', 'Face value', 'Recorded as', 'Balance after', 'When', ''].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr key={p.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{p.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)', fontFamily: 'monospace' }}>
                          {p.reference}
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(p.amount)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {naira(p.charged ?? p.amount)}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {p.balanceAfter === null ? '—' : naira(p.balanceAfter)}
                      </td>
                      <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                        {dateTime(p.createdAt)}
                      </td>
                      <td style={td}>
                        {p.atFaceValue ? (
                          <Badge variant="danger">At face value</Badge>
                        ) : p.status !== 'success' ? (
                          <Badge variant="neutral">{p.status}</Badge>
                        ) : p.commission > 0 ? (
                          <Badge variant="info">Commission collected</Badge>
                        ) : (
                          <Badge variant="success">Matches</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card padding={20}>
        <h3 style={sectionTitle}>Repairs for people&apos;s history</h3>
        <p style={sectionNote}>
          The same repairs the business accounts use, limited to the ones that
          put people&apos;s history right. Each fixes everyone at once and is
          safe to run twice. Some cover businesses too.
        </p>
        {!report ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {report.repairs.map((r) => (
                <tr key={r.key} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)', maxWidth: 640 }}>
                      {r.description}
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.outstanding > 0 ? (
                      <Badge variant="warning">{r.outstanding} to fix</Badge>
                    ) : (
                      <Badge variant="success">Nothing to fix</Badge>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {canRepair && r.outstanding > 0 && (
                      <Button
                        variant="secondary"
                        onClick={() => void runRepair(r.key)}
                        disabled={running !== null}
                      >
                        {running === r.key ? 'Running…' : 'Run'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmDialog
        isOpen={confirmFix}
        title="Correct these purchases?"
        message={
          person
            ? `${atFace.length} of ${nameOf(person)}'s purchases will be set to what Safe Haven took: the exact amount from their statement where a line matches, otherwise face value less the commission rate. Each balance after moves to match. No money moves.`
            : ''
        }
        confirmLabel="Correct them"
        loading={fixing}
        onConfirm={() => void fix()}
        onCancel={() => setConfirmFix(false)}
      />
    </div>
  );
};
